import { Controller } from "@hotwired/stimulus"
import { OnboardingApi } from "../../services/onboarding_api"

export default class extends Controller {
  static values = { sessionId: String }
  
  static targets = [
    "companyDomain", "websiteStatus",
    "companyLinkedin", "linkedinStatus",
    "companyOneliner", "summaryStatus"
  ]
  
  static outlets = ["onboarding-wizard"]

  connect() {
    this.api = new OnboardingApi(this.sessionIdValue)
    this.websiteData = null
    this.linkedinData = null
    this.lastAnalysedDomain = null
    this.lastAnalysedLinkedin = null
    this.lastAutoSummary = null
    this.isAnalysing = false
    this.isAnalysingLinkedin = false
  }

  // Auto-analyse when tabbing out of the domain field
  analyseWebsiteOnBlur(event) {
    const domain = this.hasCompanyDomainTarget ? this.companyDomainTarget.value.trim() : ""
    
    // Only auto-analyse if:
    // 1. There's a domain entered
    // 2. We haven't already analysed this domain
    // 3. We're not currently analysing
    if (!domain) return
    if (this.lastAnalysedDomain === domain) return
    if (this.isAnalysing) return
    
    this.analyseWebsite(event)
  }

  // One call, both panels (B15, night 25).
  //
  // This used to be two: `analyse_website` for the structured profile, then
  // `generate_summary` to re-summarise that profile into the box below, both on
  // gpt-4o-mini — and then the market panel asked for the same address again and
  // read the same page a third time on a different model. The endpoint now
  // answers with the profile, the summary and the market panel's three
  // suggestions from one read, and the suggestions are handed across by event
  // rather than by outlet, because no `data-*-outlet` attribute exists anywhere
  // in this app and every `static outlets` declaration in the wizard is
  // therefore dead.
  async analyseWebsite(event) {
    if (event) event.preventDefault()

    const domain = this.hasCompanyDomainTarget ? this.companyDomainTarget.value.trim() : ""
    if (!domain) {
      this.setWebsiteStatus("Enter your company's website address first.", "error")
      return
    }

    // Prevent duplicate analysis
    if (this.lastAnalysedDomain === domain && this.websiteData) {
      console.log('[companyAnalysis] Already analysed this domain, skipping')
      return
    }

    this.isAnalysing = true
    this.startElapsed("Reading your website")

    // The market panel is one click away and its three boxes are about to be
    // filled by something happening on this screen. It says so on its own status
    // line, so a person who clicks Next mid-read is not looking at three empty
    // boxes with no explanation.
    this.dispatch("started", { detail: { website: domain } })

    try {
      const data = await this.api.analyzeWebsite(domain)
      console.log('[companyAnalysis] Website data:', data)

      this.websiteData = data.website_analysis || null
      this.lastAnalysedDomain = domain

      this.applySummary(this.websiteData)

      // The market panel's three fields. Dispatched on this.element, which is
      // the wizard container both controllers are declared on, so the market
      // panel is filled by the time the person reaches it.
      this.dispatch("suggestions", { detail: { suggestions: data.suggestions || {}, website: domain } })

      this.stopElapsed()
      this.setWebsiteStatus("✓ Website read — check the summary below before continuing.", "ok")

      // Notify wizard controller
      this.dispatch("analysis-complete", { detail: { type: "website", data: this.websiteData } })
    } catch (error) {
      // error.userMessage is the server's own sentence about what went wrong:
      // blank address, unreachable address, a page with nothing on it, or the
      // model unavailable. They are four different problems with four different
      // fixes, and the fixed string this catch block used to print collapsed all
      // of them into "Could not analyse website. You can continue manually."
      console.error('[companyAnalysis] Website analysis error:', error)
      this.stopElapsed()
      const message = error.userMessage || "Something went wrong reading that website. Try again in a minute."
      this.setWebsiteStatus(message, "error")
      this.dispatch("failed", { detail: { message } })
    } finally {
      this.isAnalysing = false
    }
  }

  // The summary box is filled from the same call, not from a second one, so the
  // two descriptions of this company cannot disagree — they are one string.
  applySummary(analysis) {
    if (!this.hasCompanyOnelinerTarget) return

    const summary = (analysis && (analysis.summary || analysis.one_liner)) || ""
    if (!summary) return

    // Never overwrite something the person has already written.
    const currentValue = this.companyOnelinerTarget.value.trim()
    if (currentValue && currentValue !== this.lastAutoSummary) {
      console.log('[companyAnalysis] User has written a summary, leaving it alone')
      return
    }

    this.companyOnelinerTarget.value = summary
    this.lastAutoSummary = summary

    if (this.hasSummaryStatusTarget) {
      this.summaryStatusTarget.textContent = "✓ Summary from your website — edit it if it is not quite right"
      this.summaryStatusTarget.classList.remove('text-theme-primary', 'text-red-500')
      this.summaryStatusTarget.classList.add('text-green-600')
    }

    this.dispatch("summary-complete", { detail: { summary } })
  }

  // Six seconds is a long time to look at a line that has not moved. The strong
  // model this call now runs on takes about that long, and the target to say so
  // in was already here.
  startElapsed(label) {
    this.stopElapsed()
    this.elapsedLabel = label
    this.elapsedStartedAt = Date.now()
    this.setWebsiteStatus(`${label}…`, "working")
    this.elapsedTimer = setInterval(() => {
      const seconds = Math.round((Date.now() - this.elapsedStartedAt) / 1000)
      this.setWebsiteStatus(`${this.elapsedLabel}… ${seconds}s`, "working")
    }, 1000)
  }

  stopElapsed() {
    if (this.elapsedTimer) clearInterval(this.elapsedTimer)
    this.elapsedTimer = null
  }

  disconnect() {
    this.stopElapsed()
  }

  setWebsiteStatus(message, kind) {
    if (!this.hasWebsiteStatusTarget) return
    this.websiteStatusTarget.textContent = message
    this.websiteStatusTarget.classList.remove("text-red-500", "text-green-600", "text-theme-primary")
    if (kind === "error") {
      this.websiteStatusTarget.classList.add("text-red-500")
    } else if (kind === "ok") {
      this.websiteStatusTarget.classList.add("text-green-600")
    } else {
      this.websiteStatusTarget.classList.add("text-theme-primary")
    }
  }

  // Auto-analyse LinkedIn on blur
  analyseLinkedinOnBlur(event) {
    const linkedinUrl = this.hasCompanyLinkedinTarget ? this.companyLinkedinTarget.value.trim() : ""
    
    if (!linkedinUrl) return
    if (this.lastAnalysedLinkedin === linkedinUrl) return
    if (this.isAnalysingLinkedin) return
    
    this.analyseLinkedin(event)
  }

  async analyseLinkedin(event) {
    if (event) event.preventDefault()

    const linkedinUrl = this.hasCompanyLinkedinTarget ? this.companyLinkedinTarget.value.trim() : ""
    if (!linkedinUrl) {
      if (this.hasLinkedinStatusTarget) {
        this.linkedinStatusTarget.textContent = "Please enter a LinkedIn URL first."
        this.linkedinStatusTarget.classList.add("text-red-500")
      }
      return
    }

    // Prevent duplicate analysis
    if (this.lastAnalysedLinkedin === linkedinUrl && this.linkedinData) {
      console.log('[companyAnalysis] Already analysed this LinkedIn, skipping')
      return
    }

    this.isAnalysingLinkedin = true

    // Show loading state
    if (this.hasLinkedinStatusTarget) {
      this.linkedinStatusTarget.textContent = "Analysing LinkedIn page..."
      this.linkedinStatusTarget.classList.remove("text-red-500", "text-green-600")
      this.linkedinStatusTarget.classList.add("text-theme-primary")
    }

    try {
      const data = await this.api.analyzeLinkedin(linkedinUrl)
      console.log('[companyAnalysis] LinkedIn data:', data)
      this.linkedinData = data
      this.lastAnalysedLinkedin = linkedinUrl

      if (this.hasLinkedinStatusTarget) {
        this.linkedinStatusTarget.textContent = "✓ LinkedIn analysed successfully"
        this.linkedinStatusTarget.classList.remove("text-red-500")
        this.linkedinStatusTarget.classList.add("text-green-600")
      }

      // No summary hop here any more (night 25). This used to call
      // generate_summary, a second gpt-4o-mini call that blended the website
      // and LinkedIn records and wrote the result over the summary box —
      // discarding the Opus summary the website read had just produced and
      // paying for the privilege.
      //
      // It was never how LinkedIn reached persona generation.
      // Core::Ai::Prompts::IcpCompanyContext#linkedin_context renders SEVEN
      // fields from linkedin_analysis straight into the persona prompt —
      // company name, tagline, industry, size, specialties, about and recent
      // activity — on its own route, from the row analyse_linkedin stores. The
      // summary box was a third description of the company that nothing
      // downstream depended on.
      //
      // LinkedIn still fetches, analyses and stores exactly as it did.

      // Notify wizard controller
      this.dispatch("analysis-complete", { detail: { type: "linkedin", data } })
    } catch (error) {
      console.error('[companyAnalysis] LinkedIn analysis error:', error)
      if (this.hasLinkedinStatusTarget) {
        this.linkedinStatusTarget.textContent = "Could not analyse LinkedIn. You can continue without it."
        this.linkedinStatusTarget.classList.add("text-red-500")
      }
    } finally {
      this.isAnalysingLinkedin = false
    }
  }

  // Public method for wizard to get analysis data
  getAnalysisData() {
    return {
      website: this.websiteData,
      linkedin: this.linkedinData
    }
  }
}
